import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import {
  buildInternalTransferSettingsPayloadFromForm,
  mapInternalTransferSettingsFieldErrors,
  mapInternalTransferSettingsResponseToDto,
  validateInternalTransferSettingsPayload,
} from '../dto/system-transaction-settings.dto.js';
import {
  DEFAULT_INTERNAL_TRANSFER_FEE_PIPS,
  buildEmptyInternalTransferSettingsForm,
} from '../model/system-transaction-settings.model.js';

function formsEqual(left, right) {
  return String(left?.feePips || '') === String(right?.feePips || '');
}

export function useSystemTransactionSettings() {
  const { token } = useAuth();

  const [form, setForm] = useState(buildEmptyInternalTransferSettingsForm());
  const [initialForm, setInitialForm] = useState(buildEmptyInternalTransferSettingsForm());
  const [defaultFeePips, setDefaultFeePips] = useState(DEFAULT_INTERNAL_TRANSFER_FEE_PIPS);
  const [usingDefaultFeePips, setUsingDefaultFeePips] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formErrors, setFormErrors] = useState({});

  const applyResponse = useCallback((response) => {
    const next = mapInternalTransferSettingsResponseToDto(response);
    const nextForm = { feePips: next.feePips };

    setForm(nextForm);
    setInitialForm(nextForm);
    setDefaultFeePips(next.defaultFeePips);
    setUsingDefaultFeePips(next.usingDefaultFeePips);
  }, []);

  const loadSettings = useCallback(async () => {
    setIsLoading(true);
    setError('');
    setSuccess('');
    setFormErrors({});

    try {
      const response = await adminApi.getInternalTransferSettings(token);
      applyResponse(response);
    } catch (requestError) {
      const empty = buildEmptyInternalTransferSettingsForm();
      setForm(empty);
      setInitialForm(empty);
      setDefaultFeePips(DEFAULT_INTERNAL_TRANSFER_FEE_PIPS);
      setUsingDefaultFeePips(true);
      setError(requestError.message || 'Failed to load transaction settings');
    } finally {
      setIsLoading(false);
    }
  }, [applyResponse, token]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const onFormChange = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFormErrors((prev) => {
      if (!prev[key]) {
        return prev;
      }

      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const resetForm = useCallback(() => {
    if (isSaving) {
      return;
    }

    setForm(initialForm);
    setFormErrors({});
    setError('');
    setSuccess('');
  }, [initialForm, isSaving]);

  const saveForm = useCallback(async (event) => {
    event.preventDefault();

    const payload = buildInternalTransferSettingsPayloadFromForm(form);
    const nextErrors = validateInternalTransferSettingsPayload(payload);

    if (Object.keys(nextErrors).length > 0) {
      setFormErrors(nextErrors);
      setError('Please fix validation errors.');
      setSuccess('');
      return;
    }

    setIsSaving(true);
    setError('');
    setSuccess('');
    setFormErrors({});

    try {
      const response = await adminApi.updateInternalTransferSettings(token, {
        feePips: payload.feePips,
      });
      applyResponse(response);
      setSuccess('Transaction settings updated.');
    } catch (requestError) {
      setFormErrors(mapInternalTransferSettingsFieldErrors(requestError));
      setError(requestError.message || 'Failed to update transaction settings');
    } finally {
      setIsSaving(false);
    }
  }, [applyResponse, form, token]);

  return {
    form,
    defaultFeePips,
    usingDefaultFeePips,
    isLoading,
    isSaving,
    error,
    success,
    formErrors,
    hasChanges: !formsEqual(form, initialForm),
    onFormChange,
    resetForm,
    saveForm,
  };
}
